import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Gauge, PencilLine } from "lucide-react";
import { toast } from "sonner";
import { CountUp } from "@/components/common/CountUp";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";
import { cn } from "@/lib/utils";

const TONE = { green: "border-success bg-success/10", orange: "border-decision bg-decision/10", red: "border-destructive bg-destructive/10 alert-pulse", missing: "border-dashed border-border bg-muted/40" };
const DOT = { green: "bg-success", orange: "bg-decision", red: "bg-destructive", missing: "bg-muted-foreground/40" };
const FIELDS = [["receivables", "Créances clients (factures émises non encaissées)"], ["payables", "Dettes fournisseurs (factures reçues non payées)"], ["inventory", "Stocks (valeur, facultatif)"], ["revenue_year", "Chiffre d'affaires annuel HT (facultatif)"]];

const InputsDialog = ({ company, inputs, onClose }) => {
  const qc = useQueryClient();
  const [v, setV] = useState(Object.fromEntries(FIELDS.map(([k]) => [k, inputs[k] ?? ""])));
  const save = async (e) => {
    e.preventDefault();
    try {
      const data = (await api.put(`/business/companies/${company.id}/scoring-inputs`, Object.fromEntries(FIELDS.map(([k]) => [k, v[k] === "" ? null : Number(v[k])])))).data;
      qc.setQueryData(["biz-scoring", company.id], data); toast.success("Score mis à jour"); onClose();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-md" data-testid="scoring-inputs-dialog">
      <DialogHeader><DialogTitle>Compléter mes données</DialogTitle></DialogHeader>
      <form onSubmit={save} className="space-y-3">
        {FIELDS.map(([k, l]) => <label key={k} className="block space-y-1 text-sm font-semibold">{l}<Input type="number" min="0" step="0.01" value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} placeholder={k === "revenue_year" && inputs.revenue_estimated ? `Estimé : ${Math.round(inputs.revenue_estimated)} €` : "€"} data-testid={`scoring-input-${k}`} /></label>)}
        <p className="text-xs text-muted-foreground">Montants à date, visibles dans votre logiciel de facturation ou de comptabilité. Sans chiffre d'affaires saisi, CASHMY l'estime à partir de vos encaissements.</p>
        <Button type="submit" className="w-full rounded-full" data-testid="scoring-inputs-save">Recalculer le score</Button>
      </form>
    </DialogContent></Dialog>
  );
};

const Indicator = ({ i }) => {
  const { money } = useFormat();
  const val = i.value == null ? "À compléter" : i.unit === "€" ? money(i.value, { maximumFractionDigits: 0 }) : `${i.value}${i.unit === "%" ? " %" : ` ${i.unit}`}`;
  return (
    <li className={cn("space-y-1.5 rounded-2xl border-2 p-4", TONE[i.status])} data-testid={`scoring-ind-${i.key}`}>
      <div className="flex items-center justify-between gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground"><span className="flex items-center gap-1.5"><span className={cn("h-2.5 w-2.5 rounded-full", DOT[i.status])} />{i.label}</span>{i.points != null && <span>{i.points}/20</span>}</div>
      <div className="text-2xl font-extrabold" data-testid={`scoring-value-${i.key}`}>{val}</div>
      <p className="text-xs text-muted-foreground">{i.detail}</p>
      <p className="text-sm font-semibold">{i.advice}</p>
    </li>
  );
};

export const BizScoring = ({ company }) => {
  const [edit, setEdit] = useState(false);
  const { data } = useQuery({ queryKey: ["biz-scoring", company.id], queryFn: () => api.get(`/business/companies/${company.id}/scoring`).then((r) => r.data) });
  if (!data) return null;
  const color = data.score == null ? "text-muted-foreground" : data.score >= 75 ? "text-success" : data.score >= 55 ? "text-decision" : "text-destructive";
  return (
    <section className="card-surface space-y-4 p-5" data-testid="biz-scoring">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <div className={cn("text-5xl font-extrabold", color)} data-testid="scoring-score">{data.score == null ? "—" : <CountUp value={data.score} />}<span className="text-lg text-muted-foreground">/100</span></div>
          <div><h2 className="flex items-center gap-2 text-lg font-extrabold"><Gauge className="h-5 w-5" /> Score financier</h2><p className={cn("font-bold", color)} data-testid="scoring-level">{data.level || "Données insuffisantes"}{!data.complete && data.score != null ? " · score partiel" : ""}</p></div>
        </div>
        <Button variant="outline" className="rounded-full" onClick={() => setEdit(true)} data-testid="scoring-edit"><PencilLine className="mr-1 h-4 w-4" /> {data.complete ? "Mettre à jour mes données" : "Compléter mes données"}</Button>
      </div>
      <ul className="stagger grid gap-3 sm:grid-cols-2 lg:grid-cols-5">{data.indicators.map((i) => <Indicator key={i.key} i={i} />)}</ul>
      {edit && <InputsDialog company={company} inputs={data.inputs} onClose={() => setEdit(false)} />}
    </section>
  );
};
